import React, { useState, useEffect } from 'react';
import { Users, FileSignature, UserPlus, ShieldAlert, ChevronRight } from 'lucide-react';
import { getUsersNeedingWaiverAgreement } from '../auth/WaiverService';

const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
    <div
        className={`bg-white rounded-xl shadow-sm p-6 border-l-4 border-${color}-500`}
    >
        <div className="flex justify-between items-start">
            <div>
                <p className="text-gray-500 text-sm font-medium">{title}</p>
                <h3 className="text-3xl font-bold mt-1 text-gray-900">{value}</h3>
                {subtext && <p className="text-sm text-gray-400 mt-2">{subtext}</p>}
            </div>
            <div className={`p-3 rounded-lg bg-${color}-50 text-${color}-600`}>
                <Icon size={24} />
            </div>
        </div>
    </div>
);

const MemberStatsView = ({ players, teams, unassignedPlayers, onNavigate, onBack }) => {
    const [waiverStats, setWaiverStats] = useState({
        missingWaiverUsers: [],
        loading: true
    });

    // Calculate stats
    const totalMembers = players.length;
    const unassignedCount = unassignedPlayers.length;
    const assignedCount = totalMembers - unassignedCount; // Approximation

    // Team Breakdown
    const teamStats = teams.map(team => ({
        id: team.id,
        name: team.name,
        count: team.players ? team.players.length : 0
    })).sort((a, b) => b.count - a.count);

    useEffect(() => {
        const loadWaiverStats = async () => {
            try {
                const needyUsers = await getUsersNeedingWaiverAgreement();
                setWaiverStats({
                    missingWaiverUsers: needyUsers,
                    loading: false
                });
            } catch (error) {
                console.error("Error loading waiver stats:", error);
                setWaiverStats({ missingWaiverUsers: [], loading: false, error: true });
            }
        };

        loadWaiverStats();
    }, []);

    // Identify users with incomplete registration
    // 1. Missing Waiver (from service)
    // 2. Missing Reg Form (from unassignedPlayers prop)
    const usersMissingRegForm = unassignedPlayers.filter(p => p.hasRegistrationForm === false);

    // Combine and deduplicate
    const incompleteMap = new Map();

    waiverStats.missingWaiverUsers.forEach(u => {
        incompleteMap.set(u.userId || u.id, {
            ...u,
            name: u.name || u.displayName || u.email,
            missingWaiver: true
        });
    });

    usersMissingRegForm.forEach(u => {
        const existing = incompleteMap.get(u.id);
        if (existing) {
            existing.missingRegForm = true;
        } else {
            incompleteMap.set(u.id, {
                ...u,
                missingRegForm: true
            });
        }
    });

    const incompleteUsers = Array.from(incompleteMap.values());
    const incompleteCount = incompleteUsers.length;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 mb-6">
                <button
                    onClick={onBack}
                    className="text-gray-600 hover:text-gray-900 font-medium"
                >
                    ← Back to Overview
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Member Statistics</h1>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Total Registered"
                    value={totalMembers}
                    icon={Users}
                    color="blue"
                    subtext="Active club members"
                />

                <StatCard
                    title="Incomplete Registration"
                    value={waiverStats.loading ? '...' : incompleteCount}
                    icon={FileSignature}
                    color="red"
                    subtext="Waiver or Form needed"
                />

                <StatCard
                    title="Assigned to Team"
                    value={assignedCount}
                    icon={UserPlus}
                    color="green"
                    subtext={`${totalMembers > 0 ? ((assignedCount / totalMembers) * 100).toFixed(0) : 0}% of members`}
                />

                <StatCard
                    title="Needs Assignment"
                    value={unassignedCount}
                    icon={ShieldAlert}
                    color="orange"
                    subtext="Waiting for team"
                />
            </div>

            {/* Detailed Sections */}
            <div className="space-y-6">

                {/* Incomplete Registrations List */}
                {incompleteCount > 0 && (
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-red-100">
                        <div className="p-6 border-b border-red-100 bg-red-50 flex justify-between items-center">
                            <h3 className="font-bold text-lg text-red-900 flex items-center gap-2">
                                <ShieldAlert size={20} />
                                Action Required: Incomplete Registrations
                            </h3>
                            <span className="text-sm font-semibold bg-white text-red-700 px-3 py-1 rounded-full border border-red-200">
                                {incompleteCount} players
                            </span>
                        </div>
                        <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                            {incompleteUsers.map(user => (
                                <div key={user.id || user.userId} className="p-4 flex items-center justify-between hover:bg-gray-50">
                                    <div>
                                        <p className="font-medium text-gray-900">{user.name}</p>
                                        <p className="text-sm text-gray-500">{user.email}</p>
                                    </div>
                                    <div className="flex gap-2">
                                        {user.missingWaiver && (
                                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-medium">
                                                Missing Waiver
                                            </span>
                                        )}
                                        {user.missingRegForm && (
                                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">
                                                Missing Form
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Team Breakdown */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                            <h3 className="font-bold text-lg text-gray-900">Assignments by Team</h3>
                        </div>
                        <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                            {teamStats.map(team => (
                                <div key={team.id} className="p-4 flex justify-between items-center hover:bg-gray-50">
                                    <span className="font-medium text-gray-700">{team.name}</span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-semibold bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                                            {team.count} players
                                        </span>
                                    </div>
                                </div>
                            ))}
                            {teamStats.length === 0 && (
                                <div className="p-8 text-center text-gray-500">
                                    No teams found
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Unassigned List Preview */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-bold text-lg text-gray-900">Waiting on Assignment</h3>
                            <span className="text-sm text-gray-500">{unassignedPlayers.length} players</span>
                        </div>
                        <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                            {unassignedPlayers.slice(0, 5).map(player => (
                                <div key={player.id} className="p-4 flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-900">{player.name}</p>
                                        <p className="text-sm text-gray-500">{player.email}</p>
                                    </div>
                                    <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Pending</span>
                                </div>
                            ))}
                            {unassignedPlayers.length > 5 && (
                                <div className="p-4 text-center border-t border-gray-50">
                                    <button
                                        onClick={() => onNavigate && onNavigate('manage-players')}
                                        className="text-blue-600 font-medium text-sm hover:underline"
                                    >
                                        View all {unassignedPlayers.length} unassigned players
                                    </button>
                                </div>
                            )}
                            {unassignedPlayers.length === 0 && (
                                <div className="p-8 text-center text-gray-500">
                                    All players assigned!
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberStatsView;
